import {
  BanknotesIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  PaperClipIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import React from 'react';

import {
  MAILING_OPTIONS,
  type MailingOption,
} from '@/components/postgrid/mailing-class/MailingClassStep';
import { useGetBlobsQuery } from '@/services/api/blobService';
import { usePostgridContactsRetrieveQuery } from '@/services/api/postgridService';

import { WizardStep } from './WizardStep';

interface ChequeReviewStepProps {
  matterId?: string;
  wizardData?: {
    amount?: string;
    memo?: string;
    to_contact_id?: string;
    to_contact_name?: string;
    attachment_blob_ids?: string[];
    mailing_class?: string;
  };
  markStepComplete?: (isValid: boolean) => void;
}

export const ChequeReviewStep: React.FC<ChequeReviewStepProps> = ({
  matterId,
  wizardData = {},
  markStepComplete,
}) => {
  const { amount, memo, to_contact_id, attachment_blob_ids, mailing_class } =
    wizardData;
  const { data: contact } = usePostgridContactsRetrieveQuery(
    { id: to_contact_id as string },
    {
      skip: !to_contact_id,
    },
  );

  const { data: blobs } = useGetBlobsQuery(matterId as string, {
    skip: !matterId,
  });

  // Get selected blob objects
  const selectedBlobs = React.useMemo(() => {
    if (!blobs || !attachment_blob_ids) return [];
    return blobs.filter((blob) => attachment_blob_ids.includes(blob.id));
  }, [blobs, attachment_blob_ids]);

  // Get selected mailing option
  const selectedMailingOption = React.useMemo((): MailingOption | null => {
    if (!mailing_class) return null;
    return (
      MAILING_OPTIONS.find(
        (option: MailingOption) => option.value === mailing_class,
      ) || null
    );
  }, [mailing_class]);
  // Validate that we have all required data
  const attachmentsCount = attachment_blob_ids ? attachment_blob_ids.length : 0;
  const isValid = Boolean(amount && to_contact_id && attachmentsCount > 0);

  const formatCurrency = (value: string) => {
    const num = parseFloat(value);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(num);
  };

  return (
    <WizardStep
      title="Review Your Cheque"
      description="Please review the details below before submitting your cheque."
      isValid={isValid}
      markStepComplete={markStepComplete}
    >
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-8">
        <div className="space-y-6">
          {/* Amount */}
          <div className="text-center border-b border-green-200 pb-6">
            <div className="flex items-center justify-center mb-3">
              <BanknotesIcon className="h-8 w-8 text-green-600 mr-3" />
              <h3 className="text-2xl font-bold text-green-900">
                Cheque Amount
              </h3>
            </div>
            <div className="text-4xl font-bold text-green-800">
              {amount ? formatCurrency(amount) : 'Not specified'}
            </div>
          </div>

          {/* Recipient */}
          <div className="bg-white rounded-lg p-4 border border-green-200">
            <div className="flex items-center mb-3">
              <UserIcon className="h-5 w-5 text-green-600 mr-2" />
              <h4 className="font-medium text-green-900">Pay To</h4>
            </div>
            {contact ? (
              <div className="text-gray-900">
                <div className="font-medium text-base mb-1">
                  {contact.first_name || contact.company_name || 'Recipient'}
                  {contact.first_name &&
                    contact.last_name &&
                    ` ${contact.last_name}`}
                </div>
                <div className="text-sm text-gray-600 leading-relaxed">
                  {contact.address_line1}
                  <br />
                  {contact.address_line2 && (
                    <>
                      {contact.address_line2}
                      <br />
                    </>
                  )}
                  {contact.city}, {contact.province_or_state}{' '}
                  {contact.postal_or_zip}
                  <br />
                  {contact.country}
                </div>
              </div>
            ) : (
              <div className="text-gray-500">No recipient selected</div>
            )}
          </div>

          {/* Memo */}
          {memo && (
            <div className="bg-white rounded-lg p-4 border border-green-200">
              <div className="flex items-center mb-3">
                <DocumentTextIcon className="h-5 w-5 text-green-600 mr-2" />
                <h4 className="font-medium text-green-900">Memo</h4>
              </div>
              <div className="text-gray-900 italic">&quot;{memo}&quot;</div>
            </div>
          )}

          {/* Additional Documents */}
          <div className="bg-white rounded-lg p-4 border border-green-200">
            <div className="flex items-center mb-3">
              <PaperClipIcon className="h-5 w-5 text-green-600 mr-2" />
              <h4 className="font-medium text-green-900">
                Additional Documents
              </h4>
            </div>
            {attachment_blob_ids && attachment_blob_ids.length > 0 ? (
              <div className="space-y-2">
                <div className="text-gray-900 mb-2">
                  {attachment_blob_ids.length} document
                  {attachment_blob_ids.length !== 1 ? 's' : ''} will be included
                  with the cheque{selectedBlobs.length > 0 ? ':' : ''}
                </div>
                {selectedBlobs.length > 0 ? (
                  <div className="space-y-1">
                    {selectedBlobs.map((blob) => (
                      <div
                        key={blob.id}
                        className="text-sm text-gray-700 flex items-center"
                      >
                        <span className="w-2 h-2 bg-gray-400 rounded-full mr-2 flex-shrink-0" />
                        <span className="truncate">
                          {blob.original_filename || blob.name}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 italic">
                    Document details loading...
                  </div>
                )}
                <div className="text-sm text-gray-600 mt-2">
                  These documents will be mailed together with your cheque
                </div>
              </div>
            ) : (
              <div className="text-gray-500">
                No additional documents selected
              </div>
            )}
          </div>

          {/* Mailing Class */}
          {selectedMailingOption && (
            <div className="bg-white rounded-lg p-4 border border-green-200">
              <div className="flex items-center mb-3">
                <EnvelopeIcon className="h-5 w-5 text-green-600 mr-2" />
                <h4 className="font-medium text-green-900">Mail Service</h4>
              </div>
              <div className="space-y-2">
                <div>
                  <h5 className="font-semibold text-gray-900">
                    {selectedMailingOption.label}
                  </h5>
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedMailingOption.description}
                  </p>
                </div>
                <div className="flex items-center text-sm">
                  <span className="font-medium text-gray-700">
                    {selectedMailingOption.deliveryTime}
                  </span>
                </div>
                {selectedMailingOption.features.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {selectedMailingOption.features.map(
                      (feature: string, index: number) => (
                        <span
                          key={index}
                          className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full"
                        >
                          {feature}
                        </span>
                      ),
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Summary */}
          <div className="bg-green-100 rounded-lg p-4 border border-green-300">
            <div className="text-center">
              <p className="text-green-800 font-medium">
                ✅ Ready to create and send this cheque
              </p>
              <p className="text-sm text-green-700 mt-1">
                Once submitted, the cheque will be processed and mailed to the
                recipient.
              </p>
            </div>
          </div>
        </div>
      </div>
    </WizardStep>
  );
};
